/**
 * Product breadcrumbs from collection context (URL path, click, referrer, storage, cookie).
 */
(function () {
  'use strict';

  const COOKIE_NAME = 'kas_bc';
  const STORAGE_KEY = 'kas_bc';
  const CLICK_STORAGE_KEY = 'kas_bc_click';
  const STORAGE_MAX_AGE = 60 * 30 * 1000;
  const CLICK_MAX_AGE = 60 * 5 * 1000;
  const RETRY_DELAYS = [0, 50, 200, 500, 1200, 2500];

  function shopifyRoot() {
    let root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    if (!root.endsWith('/')) root += '/';
    return root;
  }

  function setCollectionContext(handle) {
    if (!handle) return;

    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(handle)}; path=/; max-age=${STORAGE_MAX_AGE / 1000}; SameSite=Lax`;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ handle: handle, ts: Date.now() }));
    } catch (e) {}
  }

  function readStoredHandle(key, maxAge) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;

      const stored = JSON.parse(raw);
      if (!stored || !stored.handle || !stored.ts) return null;
      if (Date.now() - stored.ts > maxAge) return null;

      return stored.handle;
    } catch (e) {
      return null;
    }
  }

  function getCollectionCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function parseCollectionHandle(urlString) {
    if (!urlString) return null;

    try {
      const url = new URL(urlString, window.location.origin);
      if (url.hostname !== window.location.hostname) return null;

      const pathMatch = url.pathname.match(/\/collections\/([^/?#]+)/);
      return pathMatch ? pathMatch[1] : null;
    } catch (e) {
      return null;
    }
  }

  function getHandleFromPath() {
    const match = window.location.pathname.match(/\/collections\/([^/]+)\/products\//);
    return match ? match[1] : null;
  }

  function getCollectionContextHandle() {
    const pathHandle = getHandleFromPath();
    if (pathHandle) return pathHandle;

    const clickHandle = readStoredHandle(CLICK_STORAGE_KEY, CLICK_MAX_AGE);
    if (clickHandle) return clickHandle;

    const referrerHandle = parseCollectionHandle(document.referrer);
    if (referrerHandle) return referrerHandle;

    const sessionHandle = readStoredHandle(STORAGE_KEY, STORAGE_MAX_AGE);
    if (sessionHandle) return sessionHandle;

    return getCollectionCookie();
  }

  function stripCollectionQueryParam() {
    if (!window.location.search.includes('collection=')) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('collection');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function getShopUrl() {
    if (window.theme && window.theme.shopUrl) return window.theme.shopUrl.replace(/\/$/, '');
    return window.location.origin;
  }

  function findBreadcrumbNav() {
    return document.querySelector('[data-kas-product-breadcrumbs]') || document.querySelector('nav.breadcrumbs');
  }

  function getProductTitle(nav) {
    const active = nav && nav.querySelector('.breadcrumbs__link.active span, .breadcrumbs__link.active');
    if (active && active.textContent.trim()) return active.textContent.trim();

    const h1 = document.querySelector('h1.product__title, .product__title');
    return h1 ? h1.textContent.trim() : '';
  }

  function getHomeLink(nav) {
    if (!nav) return null;

    return (
      nav.querySelector('a.breadcrumbs__link[href="/"]') ||
      nav.querySelector('a.breadcrumbs__link[href="' + shopifyRoot() + '"]') ||
      nav.querySelector('a.breadcrumbs__link')
    );
  }

  function updateBreadcrumbSchema(script, homeTitle, collection, productTitle, productPath) {
    if (!script) return;

    const shopUrl = getShopUrl();
    script.textContent = JSON.stringify(
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: { '@type': 'WebPage', '@id': shopUrl, name: homeTitle },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'WebPage',
              '@id': `${shopUrl}${collection.url}`,
              name: collection.title,
            },
          },
          {
            '@type': 'ListItem',
            position: 3,
            item: {
              '@type': 'WebPage',
              '@id': `${shopUrl}${productPath}`,
              name: productTitle,
            },
          },
        ],
      },
      null,
      2
    );
  }

  function breadcrumbShowsCollection(nav, handle) {
    const expected = `/collections/${handle}`;
    return Array.from(nav.querySelectorAll('a.breadcrumbs__link[href]')).some((link) => {
      const href = link.getAttribute('href') || '';
      return href === expected || href.endsWith(expected);
    });
  }

  function rebuildBreadcrumbs(nav, collection) {
    const homeLink = getHomeLink(nav);
    const productTitle = getProductTitle(nav);
    const schemaScript = nav.querySelector('script[type="application/ld+json"]');
    const sepEl = nav.querySelector('.breadcrumbs__sep');

    if (!homeLink || !productTitle) return false;

    const homeTitle = (homeLink.textContent || 'Home').trim();
    const productPath = window.location.pathname;
    const sep = sepEl ? sepEl.outerHTML : '<span class="breadcrumbs__sep">/</span>';

    const homeClone = homeLink.cloneNode(true);
    const collectionLink = document.createElement('a');
    collectionLink.href = collection.url;
    collectionLink.className = 'breadcrumbs__link';
    collectionLink.innerHTML = `<span>${collection.title}</span>`;

    const productSpan = document.createElement('span');
    productSpan.className = 'breadcrumbs__link active';
    productSpan.innerHTML = `<span>${productTitle}</span>`;

    Array.from(nav.childNodes).forEach((node) => {
      if (node === schemaScript) return;
      nav.removeChild(node);
    });

    nav.insertBefore(homeClone, schemaScript);
    nav.insertBefore(document.createRange().createContextualFragment(sep), schemaScript);
    nav.insertBefore(collectionLink, schemaScript);
    nav.insertBefore(document.createRange().createContextualFragment(sep), schemaScript);
    nav.insertBefore(productSpan, schemaScript);

    updateBreadcrumbSchema(schemaScript, homeTitle, collection, productTitle, productPath);
    nav.setAttribute('data-kas-bc-applied', collection.handle);
    return true;
  }

  async function applyCollectionBreadcrumb(handle) {
    const nav = findBreadcrumbNav();
    if (!nav || !handle) return false;

    if (nav.getAttribute('data-kas-bc-applied') === handle) return true;
    if (breadcrumbShowsCollection(nav, handle)) {
      nav.setAttribute('data-kas-bc-applied', handle);
      return true;
    }

    const root = shopifyRoot();
    const res = await fetch(`${root}collections/${encodeURIComponent(handle)}.json`);
    if (!res.ok) return false;

    const data = await res.json();
    const collection = data && data.collection;
    if (!collection || !collection.url) return false;

    return rebuildBreadcrumbs(nav, collection);
  }

  function tryApply() {
    const handle = getCollectionContextHandle();
    if (!handle) return;

    setCollectionContext(handle);
    applyCollectionBreadcrumb(handle);
  }

  function initProductPage() {
    if (!/\/products\//.test(window.location.pathname)) return;

    stripCollectionQueryParam();

    const pathHandle = getHandleFromPath();
    if (pathHandle) return;

    RETRY_DELAYS.forEach((delay) => {
      setTimeout(tryApply, delay);
    });

    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(tryApply);
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 8000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductPage);
  } else {
    initProductPage();
  }
})();
